import React, { useEffect, useState } from 'react';
import { useProviderBillingInfo } from '@features/user';
import { useUpdateUserPreferencesMutation, useUserPreferences } from '@features/user-preferences';
import { Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

interface CopyPastePreferencesFormProps {
  onSuccessfulFormSubmission?: () => void;
}

const CopyPastePreferencesForm: React.FC<CopyPastePreferencesFormProps> = ({
  onSuccessfulFormSubmission,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { preferences, isLoading: isLoadingPreferences } = useUserPreferences();
  const providerBillingInfo = useProviderBillingInfo();
  const [updateUserPreferences, { isLoading: isSaving }] = useUpdateUserPreferencesMutation();

  // Local state for the checkboxes
  const [includeHeader, setIncludeHeader] = useState<boolean>(true);
  const [htmlFormatting, setHtmlFormatting] = useState<boolean>(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState<{
    includeHeader: boolean;
    htmlFormatting: boolean;
  }>({ includeHeader: true, htmlFormatting: true });

  // Initialize from user preferences
  useEffect(() => {
    const headerValue = preferences?.copy_paste_preferences?.include_header_on_copy_multiple ?? true;
    const htmlValue = preferences?.copy_paste_preferences?.html_formatting_on_copy ?? true;
    
    setIncludeHeader(headerValue);
    setHtmlFormatting(htmlValue);
    setOriginalValues({ 
      includeHeader: headerValue, 
      htmlFormatting: htmlValue 
    });
    setHasChanges(false);
  }, [preferences]);

  // Handle checkbox changes
  const handleHeaderCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setIncludeHeader(newValue);
    setHasChanges(
      newValue !== originalValues.includeHeader || 
      htmlFormatting !== originalValues.htmlFormatting
    );
  };

  const handleHtmlCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setHtmlFormatting(newValue);
    setHasChanges(
      includeHeader !== originalValues.includeHeader || 
      newValue !== originalValues.htmlFormatting
    );
  };

  // Save preferences
  const handleSave = async () => {
    try {
      await updateUserPreferences({
        updateUserPreferencesRequest: {
          copy_paste_preferences: {
            include_header_on_copy_multiple: includeHeader,
            html_formatting_on_copy: htmlFormatting,
          },
        },
      }).unwrap();

      setOriginalValues({ 
        includeHeader, 
        htmlFormatting 
      });
      setHasChanges(false);
      onSuccessfulFormSubmission?.();
    } catch (error) {
      console.error('Failed to save copy/paste preferences:', error);
      enqueueSnackbar('Failed to save copy/paste preferences', { variant: 'error' });
    }
  };

  // Cancel changes
  const handleCancel = () => {
    setIncludeHeader(originalValues.includeHeader);
    setHtmlFormatting(originalValues.htmlFormatting);
    setHasChanges(false);
  };

  if (isLoadingPreferences) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        {/* Description */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Copy/Paste Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure how notes are copied when using the bulk copy features.
          </Typography>
        </Box>

        {/* Settings */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeHeader}
                  onChange={handleHeaderCheckboxChange}
                  disabled={isSaving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Include header when copying multiple sections
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    When enabled, the note header (provider name, date, time, duration) will be
                    included when using &quot;Copy All Sections&quot; or &quot;Copy Selected Sections&quot;. This does
                    not affect copying individual sections.
                  </Typography>
                </Box>
              }
            />
            
            {/* Preview Box for header */}
            {includeHeader && (
              <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                <Typography variant="caption" gutterBottom color="text.secondary">
                  Preview of header that will be included:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    backgroundColor: 'grey.50',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{ margin: 0, fontFamily: 'inherit', fontSize: 'inherit' }}
                  >
                    {`Provider: ${providerBillingInfo?.first_name || 'John'} ${providerBillingInfo?.last_name || 'Smith'}
Date: ${format(new Date(), 'MM/dd/yyyy')}
Time: 10:00 AM - 10:50 AM
Duration: 50 minutes`}
                  </Typography>
                </Paper>
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={htmlFormatting}
                  onChange={handleHtmlCheckboxChange}
                  disabled={isSaving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Enable HTML formatting when copying
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Most users should keep this enabled for better formatting when pasting into modern EHRs. 
                    Only disable if your EHR system doesn&apos;t support HTML formatting or if you experience 
                    issues with pasted content.
                  </Typography>
                </Box>
              }
            />
          </FormGroup>
          
          {/* Warning when HTML formatting is disabled */}
          {!htmlFormatting && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>HTML formatting is disabled.</strong> When you copy notes, only plain text will be copied 
                to your clipboard. This means formatting like bold text, lists, and proper spacing may be lost 
                when pasting. This setting is only recommended for legacy EHR systems that don&apos;t support 
                formatted text.
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Unsaved changes alert */}
        {hasChanges && (
          <Alert severity="warning">You have unsaved changes to your copy/paste preferences.</Alert>
        )}

        {/* Action buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          {hasChanges && (
            <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
          )}
        </Stack>

        <Divider />

        {/* Additional info */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            These settings apply to all notes you copy from the application. Individual section
            copying never includes the header, regardless of this setting.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default CopyPastePreferencesForm;
